<script setup lang="ts">
/**
 * THE VENUE: the company on its receipts and the tables on its floor.
 *
 * Saved here, reaching the tills when published — one button, and every till
 * fetches the new bundle at once. Until then the screen says there are changes
 * the tills do not have. Read-only for anybody who may not configure the venue
 * (`venue.configure`: owner, admin).
 */
import { computed, onMounted, reactive, ref } from "vue";
import type { ConsoleApi, VenueSettings, VenueTable } from "../api";

type VenueApi = Pick<ConsoleApi, "venueSettings" | "updateIdentity" | "saveTable" | "publishVenue" | "setGuestOrdering">;

const props = defineProps<{
  api: VenueApi;
  me: { roleId: string } | null;
  t: (key: string) => string;
  /** For "2 místa" and "5 míst": Czech has more than one plural. */
  locale?: string;
}>();

const seats = (n: number): string => {
  const form = new Intl.PluralRules(props.locale ?? "cs").select(n);
  const key = `venue.seats.${form}`;
  const word = props.t(key);
  return `${n} ${word === key ? props.t("venue.seats.other") : word}`;
};

const settings = ref<VenueSettings | null>(null);
const status = ref<"loading" | "ready" | "failed">("loading");
const busy = ref(false);
const unpublished = ref(false);
const notice = ref<{ tone: "ok" | "bad"; text: string } | null>(null);
const editing = ref<VenueTable | "new" | null>(null);

const canConfigure = computed(() => props.me === null || ["owner", "admin"].includes(props.me.roleId));

const form = reactive({
  legalName: "",
  registrationId: "",
  vatId: "",
  legalAddress: "",
  fiscalRegistrationId: "",
  fiscalUnitId: "",
});
const table = reactive({ name: "", seats: "2", area: "", active: true });

const load = async (): Promise<void> => {
  try {
    const s = await props.api.venueSettings();
    settings.value = s;
    // The server compares what the tills have with what is saved, so a reload still says so.
    unpublished.value = s.published?.current === false;
    form.legalName = s.identity.legalName ?? "";
    form.registrationId = s.identity.registrationId ?? "";
    form.vatId = s.identity.vatId ?? "";
    form.legalAddress = s.identity.legalAddress ?? "";
    form.fiscalRegistrationId = s.identity.fiscalRegistrationId ?? "";
    form.fiscalUnitId = s.identity.fiscalUnitId === null ? "" : String(s.identity.fiscalUnitId);
    status.value = "ready";
  } catch {
    status.value = "failed";
  }
};
onMounted(load);

const areas = computed(() => {
  const by = new Map<string, VenueTable[]>();
  for (const t of settings.value?.floor ?? []) by.set(t.area, [...(by.get(t.area) ?? []), t]);
  return [...by.entries()];
});

const run = async (write: () => Promise<unknown>, ok: string): Promise<boolean> => {
  busy.value = true;
  notice.value = null;
  try {
    await write();
    notice.value = { tone: "ok", text: props.t(ok) };
    return true;
  } catch (e) {
    notice.value = { tone: "bad", text: e instanceof Error && e.message ? e.message : props.t("common.failed") };
    return false;
  } finally {
    busy.value = false;
  }
};

const saveIdentity = async (): Promise<void> => {
  const orNull = (v: string) => (v.trim() === "" ? null : v.trim());
  const saved = await run(
    () =>
      props.api.updateIdentity({
        legalName: orNull(form.legalName),
        registrationId: orNull(form.registrationId),
        vatId: orNull(form.vatId),
        legalAddress: orNull(form.legalAddress),
        fiscalRegistrationId: orNull(form.fiscalRegistrationId),
        fiscalUnitId: form.fiscalUnitId.trim() === "" ? null : Number(form.fiscalUnitId),
      }),
    "venue.saved",
  );
  if (saved) unpublished.value = true;
};

const openTable = (t: VenueTable | "new"): void => {
  if (!canConfigure.value) return;
  editing.value = t;
  table.name = t === "new" ? "" : t.name;
  table.seats = t === "new" ? "2" : String(t.seats);
  table.area = t === "new" ? (areas.value[0]?.[0] ?? "") : t.area;
  table.active = t === "new" ? true : t.active;
};

const tableValid = computed(() => table.name.trim().length > 0 && /^\d{1,3}$/.test(table.seats));

const saveTable = async (): Promise<void> => {
  const target = editing.value;
  if (!target || !tableValid.value) return;
  const saved = await run(
    () =>
      props.api.saveTable(target === "new" ? null : target.tableRef, {
        name: table.name.trim(),
        seats: Number(table.seats),
        area: table.area.trim(),
        active: table.active,
      }),
    "venue.saved",
  );
  if (saved) {
    unpublished.value = true;
    editing.value = null;
    await load();
  }
};

/** Live at once: the server checks it on every order a phone sends. */
const setOrdering = async (on: boolean): Promise<void> => {
  const saved = await run(() => props.api.setGuestOrdering(on), "venue.saved");
  if (saved && settings.value) settings.value = { ...settings.value, guestOrdering: on };
};

const publish = async (): Promise<void> => {
  const ok = await run(() => props.api.publishVenue(), "venue.published");
  if (ok) {
    unpublished.value = false;
    await load();
  }
};
</script>

<template>
  <section class="venue">
    <p v-if="status === 'loading'" class="gf-muted" role="status">{{ t("common.loading") }}</p>
    <div v-else-if="status === 'failed'" class="gf-note gf-note--bad" role="alert">
      {{ t("common.failed") }}
      <button type="button" class="gf-button" @click="load">{{ t("common.retry") }}</button>
    </div>

    <template v-else-if="settings">
      <header class="venue__bar">
        <span class="gf-muted">{{ t("venue.version") }} {{ settings.published?.version ?? 0 }}</span>
        <span v-if="unpublished" class="venue__draft" role="status">{{ t("venue.unpublished") }}</span>
        <!--
          Red only when there is something to publish: a primary that is
          always there stops meaning "do this now". A venue that has never
          published is the case that most needs it.
        -->
        <button
          v-if="canConfigure"
          type="button"
          class="gf-button"
          :class="{ 'gf-button--primary': unpublished || !settings.published }"
          data-testid="publish-venue"
          :disabled="busy"
          @click="publish"
        >
          {{ t("venue.publish") }}
        </button>
      </header>

      <p v-if="notice" class="venue__notice" :class="`venue__notice--${notice.tone}`" role="status">{{ notice.text }}</p>

      <div class="venue__columns">
        <form class="gf-panel venue__identity" @submit.prevent="saveIdentity">
          <h2 class="gf-panel__title">{{ t("venue.company") }}</h2>
          <p class="gf-muted venue__lead">{{ t("venue.companyLead") }}</p>
          <p v-if="!canConfigure" class="venue__readOnly" role="note">{{ t("venue.ownerOnly") }}</p>
          <label class="gf-field">
            <span class="gf-label">{{ t("venue.legalName") }}</span>
            <input v-model="form.legalName" class="gf-input" :disabled="!canConfigure" data-field="legalName" />
          </label>
          <div class="venue__pair">
            <label class="gf-field">
              <span class="gf-label">{{ t("venue.registrationId") }}</span>
              <input v-model="form.registrationId" class="gf-input venue__code" inputmode="numeric" :disabled="!canConfigure" data-field="registrationId" />
            </label>
            <label class="gf-field">
              <span class="gf-label">{{ t("venue.vatId") }}</span>
              <input v-model="form.vatId" class="gf-input venue__code" :disabled="!canConfigure" data-field="vatId" />
            </label>
          </div>
          <label class="gf-field">
            <span class="gf-label">{{ t("venue.address") }}</span>
            <input v-model="form.legalAddress" class="gf-input" :disabled="!canConfigure" data-field="legalAddress" />
          </label>
          <div class="venue__pair">
            <label class="gf-field">
              <span class="gf-label">{{ t("venue.eic") }}</span>
              <input v-model="form.fiscalRegistrationId" class="gf-input venue__code" :disabled="!canConfigure" data-field="eic" />
            </label>
            <label class="gf-field">
              <span class="gf-label">{{ t("venue.unit") }}</span>
              <input v-model="form.fiscalUnitId" class="gf-input venue__code" inputmode="numeric" :disabled="!canConfigure" data-field="unit" />
            </label>
          </div>
          <div v-if="canConfigure" class="venue__actions">
            <button type="submit" class="gf-button" :disabled="busy" data-testid="save-identity">{{ t("menu.save") }}</button>
          </div>
        </form>

        <div class="gf-panel venue__floor">
          <div class="venue__floorHead">
            <h2 class="gf-panel__title">{{ t("venue.tables") }}</h2>
            <button v-if="canConfigure" type="button" class="gf-button" data-testid="add-table" @click="openTable('new')">
              {{ t("venue.addTable") }}
            </button>
          </div>
          <p v-if="areas.length === 0 && !editing" class="gf-muted venue__lead">{{ t("venue.noTables") }}</p>
          <div v-for="[area, tables] in areas" :key="area" class="venue__area">
            <h3 class="venue__areaName">{{ area || t("venue.noArea") }}</h3>
            <ul class="venue__tables">
              <li v-for="tb in tables" :key="tb.tableRef">
                <button
                  type="button"
                  class="venue__table"
                  :class="{ 'venue__table--retired': !tb.active }"
                  :data-table="tb.tableRef"
                  :disabled="!canConfigure"
                  @click="openTable(tb)"
                >
                  <span class="venue__tableName">{{ tb.name }}</span>
                  <span class="gf-muted">{{ seats(tb.seats) }}</span>
                </button>
              </li>
            </ul>
          </div>

          <form v-if="editing" class="venue__tableForm" @submit.prevent="saveTable">
            <h3 class="venue__formTitle">{{ editing === "new" ? t("venue.newTable") : t("venue.editTable") }}</h3>
            <label class="gf-field">
              <span class="gf-label">{{ t("venue.tableName") }}</span>
              <input v-model="table.name" class="gf-input" data-field="tableName" />
            </label>
            <div class="venue__pair">
              <label class="gf-field">
                <span class="gf-label">{{ t("venue.seatsLabel") }}</span>
                <input v-model="table.seats" class="gf-input gf-num" inputmode="numeric" data-field="tableSeats" />
              </label>
              <label class="gf-field">
                <span class="gf-label">{{ t("venue.area") }}</span>
                <input v-model="table.area" class="gf-input" data-field="tableArea" list="venue-areas" />
                <datalist id="venue-areas">
                  <option v-for="[area] in areas" :key="area" :value="area" />
                </datalist>
              </label>
            </div>
            <label v-if="editing !== 'new'" class="venue__check">
              <input v-model="table.active" type="checkbox" data-field="tableActive" /> {{ t("venue.tableActive") }}
            </label>
            <div class="venue__actions">
              <button type="button" class="gf-button" @click="editing = null">{{ t("menu.cancel") }}</button>
              <button type="submit" class="gf-button" :disabled="!tableValid || busy" data-testid="save-table">
                {{ t("menu.save") }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <section class="gf-panel venue__guests">
        <h2 class="gf-panel__title">{{ t("venue.guests") }}</h2>
        <label class="venue__check">
          <input
            type="checkbox"
            data-field="guestOrdering"
            :checked="settings.guestOrdering === true"
            :disabled="!canConfigure || busy"
            @change="setOrdering(($event.target as HTMLInputElement).checked)"
          />
          {{ t("venue.guestOrdering") }}
        </label>
        <p class="gf-muted venue__lead">{{ t("venue.guestOrderingLead") }}</p>
      </section>
    </template>
  </section>
</template>

<style scoped>
.venue {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.venue__bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--gf-space-3);
}

/* A status pill: the tint says "not live yet", the words say what. */
.venue__draft {
  padding: 0 var(--gf-space-2);
  border-radius: var(--gf-radius-full);
  background: var(--gf-color-warning-surface);
  color: var(--gf-color-warning);
  font-size: var(--gf-text-xs);
  font-weight: var(--gf-weight-semibold);
  line-height: 1.6;
}

.venue__notice {
  margin: 0;
  padding: var(--gf-space-3) var(--gf-space-4);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface-sunken);
}

.venue__notice--bad {
  color: var(--gf-color-danger);
  background: var(--gf-color-danger-surface);
}

.venue__columns {
  display: grid;
  grid-template-columns: minmax(20rem, 1fr) minmax(20rem, 1fr);
  gap: var(--gf-space-4);
  align-items: start;
}

.venue__identity,
.venue__guests,
.venue__floor {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.venue__lead {
  margin: 0;
  font-size: var(--gf-text-sm);
}

/* Two fields side by side where they fit, stacked where they do not — "1fr 1fr"
   held them side by side at phone width and the page scrolled sideways. */
.venue__pair {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: var(--gf-space-3);
}

.venue__pair > .gf-field,
.venue__identity .gf-input {
  min-width: 0;
}

/* Identifiers are read character by character, left to right — not figures. */
.venue__code {
  font-variant-numeric: tabular-nums;
}

.venue__readOnly {
  margin: 0;
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
}

/*
 * A field nobody may change must not look like one somebody can. Scoped here
 * rather than waiting on a shared `.gf-input:disabled`.
 */
.venue .gf-input:disabled {
  color: var(--gf-color-text-muted);
  background: var(--gf-color-surface-sunken);
  border-color: var(--gf-color-border-subtle);
  cursor: not-allowed;
}

.venue__formTitle {
  margin: 0;
  font-size: var(--gf-text-base);
  font-weight: var(--gf-weight-semibold);
}

.venue__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gf-space-2);
}

.venue__floorHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.venue__areaName {
  margin: 0 0 var(--gf-space-2);
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text-muted);
}

.venue__tables {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gf-space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.venue__table {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 6rem;
  padding: var(--gf-space-2) var(--gf-space-3);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface);
  color: var(--gf-color-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.venue__table:hover:not(:disabled) {
  background: var(--gf-color-surface-hover);
}

.venue__table:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

.venue__table:disabled {
  cursor: default;
}

.venue__table--retired {
  opacity: 0.5;
  border-color: var(--gf-color-border);
  border-style: dashed;
}

.venue__tableName {
  font-weight: var(--gf-weight-semibold);
}

.venue__tableForm {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
  padding-top: var(--gf-space-3);
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.venue__check {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
}

@media (max-width: 900px) {
  .venue__columns {
    grid-template-columns: 1fr;
  }
}
</style>
